import { useMemo } from 'react';

/**
 * Custom hook for calculating validation metrics from stores and validation results
 * @param {Array} stores - Array of store objects
 * @param {Array} validationResults - Array of validation result objects
 * @returns {Object} Calculated metrics including averageScore, statusCounts, issueCategories
 */
export function useValidationMetrics(stores = [], validationResults = []) {
    const metrics = useMemo(() => {
        const calculatedMetrics = {
            totalStores: stores.length,
            validatedStores: validationResults.length,
            averageScore: 0,
            issueCategories: {
                offers: 0,
                trackingNumbers: 0,
                counties: 0,
                globalRevisions: 0
            },
            statusCounts: {
                excellent: 0, // 90-100%
                good: 0,      // 70-89%
                warning: 0,   // 50-69%
                critical: 0   // 0-49%
            }
        };

        if (validationResults.length > 0) {
            const totalScore = validationResults.reduce((sum, result) => sum + (result.overallScore || 0), 0);
            calculatedMetrics.averageScore = Math.round(totalScore / validationResults.length);

            validationResults.forEach(result => {
                // Count status categories
                const score = result.overallScore || 0;
                if (score >= 90) calculatedMetrics.statusCounts.excellent++;
                else if (score >= 70) calculatedMetrics.statusCounts.good++;
                else if (score >= 50) calculatedMetrics.statusCounts.warning++;
                else calculatedMetrics.statusCounts.critical++;

                // Count issues by category
                if (result.issues) {
                    result.issues.forEach(issue => {
                        if (issue.type.includes('offer')) calculatedMetrics.issueCategories.offers++;
                        else if (issue.type.includes('tracking')) calculatedMetrics.issueCategories.trackingNumbers++;
                        else if (issue.type.includes('county') || issue.type.includes('counties')) calculatedMetrics.issueCategories.counties++;
                        else if (issue.type.includes('revision')) calculatedMetrics.issueCategories.globalRevisions++;
                    });
                }
            });
        }

        return calculatedMetrics;
    }, [stores, validationResults]);

    return metrics;
}

export default useValidationMetrics;
